import clsx from 'clsx'
import React from 'react'
import { Logger } from 'wdc-cube'
import { bindUpdate } from 'wdc-cube-react'

import { makeStyles } from 'tss-react/mui'

import { Divider, Grid, Paper } from '@mui/material'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import GridItem from 'src/components/grid-item/GridItem'
import { classToFComponent, FCClassContext } from 'src/utils/views'
import PJScope, { CompanyPartner, CompanyRow } from '../pj.scopes'

const LOG = Logger.get('PJ-VIEW')

const useStyles = makeStyles()({
    view: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        padding: 10,
        minHeight: 600,
        rowGap: 10
    },
    cnpjInput: {
        flexGrow: 1
    },
    syncButton: {
        alignSelf: 'center'
    }
})

const partnerColumns = static_build_partner_columns()
const foneNumbersColumns = static_build_fone_numbers_columns()
const emailsColumns = static_build_emails_columns()
const filiaisColumns = static_build_filiais_columns()

type PJViewProps = {
    className?: string
    scope: PJScope
}
class PJViewClass implements FCClassContext<PJViewProps> {
    // :: Fields
    scope!: PJScope
    sytles!: ReturnType<typeof useStyles>
    cnpjInputRef!: React.MutableRefObject<HTMLInputElement | undefined>

    // :: emissors

    readonly emitOnFind = () => {
        this.scope.cnpjSelected = this.cnpjInputRef.current?.value || ''
        this.scope.onFind().catch(LOG.caught)
    }

    // :: Methods

    onSyncState({ scope }: PJViewProps) {
        this.scope = scope
        this.sytles = useStyles()
    }

    render({ className, scope }: PJViewProps) {
        this.scope = bindUpdate(React, scope)
        this.cnpjInputRef = React.useRef<HTMLInputElement>()

        const classes = this.sytles.classes

        return (
            <div className={clsx(classes.view, className)}>
                <Box display="flex" flexDirection="row" alignItems="baseline" gap="10px">
                    <TextField
                        className={classes.cnpjInput}
                        inputRef={this.cnpjInputRef}
                        label="Cole ou escreva o CNPJ aqui"
                        variant="standard"
                    />
                    <Button variant="outlined" onClick={this.emitOnFind}>
                        Buscar
                    </Button>
                </Box>
                {this.scope.company.map(this.createCompany)}
            </div>
        )
    }

    createCompany(company: CompanyRow, index: number) {
        return (
            <Paper key={index} elevation={3}>
                <Grid container spacing={1} paddingTop={2} paddingBottom={2} paddingLeft={5} paddingRight={5}>
                    <GridItem label="CNPJ" value={company.CnpjCpf} xs={4} />
                    <GridItem label="Nome" value={company.Nome} xs={4} />
                    <GridItem label="Fantasia" value={company.NomeFantasia} xs={4} />

                    <GridItem label="Data Abertura" value={company.ReceitaAbertura} xs={4} />
                    <GridItem label="Porte" value={company.Porte} xs={4} />
                    <GridItem label="Situação" value={company.ReceitaSituacao} xs={4} />
                    <GridItem label="Qtd Funcionários" value={company.qtdFunc ? company.qtdFunc.toFixed(0) : '?'} xs={4} />
                    <GridItem label="Capital Social" value={company.ReceitaCapitalSocial.toFixed(2)} xs={4} />
                </Grid>

                <Divider textAlign="center">Atividade Econômica</Divider>

                <Grid container spacing={1} paddingTop={2} paddingBottom={2} paddingLeft={5} paddingRight={5}>
                    <GridItem label="Código Ibge" value={company.CodIbge} xs={4} />
                    <GridItem label="NaturezaJuridica" value={company.ReceitaNaturezaJuridica} xs={8} />
                    <GridItem label="Atividade Econômica" value={company.ReceitaAtividade} xs={4} />
                    <GridItem label="Atividade Secundária" value={company.ReceitaAtividadeSecundaria} xs={8} />
                </Grid>

                <Divider textAlign="center">Regime Tributário</Divider>

                <Grid container spacing={1} paddingTop={2} paddingBottom={2} paddingLeft={5} paddingRight={5}>
                    <GridItem label="CNAE" value={company.VaduAtividadeCNAE} xs={12} />
                    <GridItem label="ReceitaMotivoSituacao" value={company.ReceitaMotivoSituacao} xs={4} />
                    <GridItem label="ReceitaDataSituacao" value={company.ReceitaDataSituacao} xs={8} />
                    <GridItem label="Situação Especial" value={company.ReceitaSituacaoEspecial} xs={4} />
                    <GridItem label="Data Situação Especial" value={company.ReceitaDataSituacaoEspecial} xs={8} />
                    <GridItem label="MEI" value={company.CodigoMEI ? 'Sim' : 'Não'} xs={4} />
                    <GridItem label="MEI Data Consulta" value={company.CodigoMEIDataConsulta} xs={8} />
                    <GridItem label="Simples Nacional" value={company.SimplesNacional ? 'Sim' : 'Não'} xs={4} />
                    <GridItem
                        label="Simples Nacional Data Consulta"
                        value={company.SimplesNacionalDataConsulta}
                        xs={8}
                    />
                </Grid>

                <Divider textAlign="center">Contato</Divider>

                <Grid container spacing={1} paddingTop={2} paddingBottom={2} paddingLeft={5} paddingRight={5}>
                    <GridItem label="Site" value={company.Site} xs={4} />
                    <GridItem label="Email" value={company.EmailPrincipal} xs={4} />
                    <GridItem label="Telefone" value={company.TelefonePrincipal} xs={4} />
                </Grid>

                <Divider textAlign="center">Endereço</Divider>

                <Grid container spacing={1} paddingTop={2} paddingBottom={2} paddingLeft={5} paddingRight={5}>
                    <GridItem label="Logradouro" value={company.Logradouro} xs={8} />
                    <GridItem label="Número" value={company.NumeroLogradouro} xs={4} />
                    <GridItem label="Complemento" value={company.ComplementoEndereco} xs={12} />
                    <GridItem label="Bairro" value={company.BairroEndereco} xs={12} />
                    <GridItem label="Municipio" value={company.MunicipioEndereco} xs={8} />
                    <GridItem label="UF" value={company.UfEndereco} xs={4} />
                    <GridItem label="Cep" value={company.CepEnderecoFormatado} xs={4} />
                </Grid>

                {company.ReceitaTipo !== 'MATRIZ' && (
                    <>
                        <Divider textAlign="center">Dados da Matriz</Divider>

                        <Grid container spacing={1} paddingTop={2} paddingBottom={2} paddingLeft={5} paddingRight={5}>
                            <GridItem label="CNPJ" value={company.CnpjMatriz} xs={4} />
                            <GridItem label="Qtd. Funcionários" value={company.qtdFuncMatriz.toFixed(0)} xs={4} />
                            <GridItem
                                label="Capital Social"
                                value={company.ReceitaMatrizCapitalSocial.toFixed(2)}
                                xs={4}
                            />
                        </Grid>
                    </>
                )}

                <Divider textAlign="center">Detalhes</Divider>

                <Grid container spacing={1} paddingTop={2} paddingBottom={2} paddingLeft={5} paddingRight={5}>
                    {company.Filiais && company.Filiais.length && (
                        <Grid item xs={6}>
                            <DataGrid
                                rows={company.Filiais.map((item, idx) => ({ id: idx, value: item })) || []}
                                columns={filiaisColumns}
                                initialState={{
                                    pagination: {
                                        paginationModel: {
                                            pageSize: 10
                                        }
                                    }
                                }}
                            />
                        </Grid>
                    )}
                    {company.Telefones && company.Telefones.length && (
                        <Grid item xs={6}>
                            <DataGrid
                                rows={company.Telefones.map((item, idx) => ({ id: idx, value: item })) || []}
                                columns={foneNumbersColumns}
                                initialState={{
                                    pagination: {
                                        paginationModel: {
                                            pageSize: 10
                                        }
                                    }
                                }}
                            />
                        </Grid>
                    )}
                    {company.Emails && company.Emails.length && (
                        <Grid item xs={6}>
                            <DataGrid
                                rows={company.Emails.map((item, idx) => ({ id: idx, value: item })) || []}
                                columns={emailsColumns}
                                initialState={{
                                    pagination: {
                                        paginationModel: {
                                            pageSize: 10
                                        }
                                    }
                                }}
                            />
                        </Grid>
                    )}
                </Grid>

                <Divider textAlign="center">Sócios</Divider>

                <Grid container spacing={1} paddingTop={2} paddingBottom={2} paddingLeft={5} paddingRight={5}>
                    <Grid item xs={12}>
                        <DataGrid
                            rows={company.Socios || []}
                            columns={partnerColumns}
                            getRowId={static_getPartnerRowId}
                            initialState={{
                                pagination: {
                                    paginationModel: {
                                        pageSize: 10
                                    }
                                }
                            }}
                        />
                    </Grid>
                </Grid>
            </Paper>
        )
    }
}

export const PJView = classToFComponent(PJViewClass, React)
export default PJView

function static_getPartnerRowId(row: CompanyPartner) {
    return row.CpfCnpj ?? '00000000000000'
}

function static_build_partner_columns(): GridColDef[] {
    return [
        {
            field: 'CpfCnpj',
            headerName: 'CPF/CNPJ',
            width: 100
        },
        {
            field: 'Nome',
            headerName: 'Nome',
            width: 300
        },
        {
            field: 'Qualificacao',
            headerName: 'Qualificação',
            width: 300
        },
        {
            field: 'QualificacaoRepresentanteLegal',
            headerName: 'Qual. Rep. Legal',
            width: 300
        }
    ]
}

function static_build_filiais_columns(): GridColDef[] {
    return [
        {
            field: 'value',
            headerName: 'Filial',
            width: 300
        }
    ]
}

function static_build_fone_numbers_columns(): GridColDef[] {
    return [
        {
            field: 'value',
            headerName: 'Telefone',
            width: 300
        }
    ]
}

function static_build_emails_columns(): GridColDef[] {
    return [
        {
            field: 'value',
            headerName: 'Email',
            width: 300
        }
    ]
}
